import React, { useState, useRef, useEffect } from 'react';
import { Label } from '../ui/label';
import { HelpCircle, X } from 'lucide-react';

// ==================== SECTION WRAPPER ====================
export const Section = ({ title, icon, children, collapsible = false, defaultCollapsed = false, sectionId, onPhoto }) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const cameraRef = useRef(null);

  useEffect(() => {
    if (collapsible && defaultCollapsed) {
      setIsCollapsed(true);
    }
  }, [collapsible, defaultCollapsed]);

  return (
    <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden">
      <div
        className={`p-6 ${collapsible ? 'cursor-pointer hover:bg-stone-50 transition-colors' : ''}`}
        onClick={collapsible ? () => setIsCollapsed(!isCollapsed) : undefined}
      >
        <h2 className="text-xl font-bold text-stone-800 font-heading flex items-center gap-3">
          {icon}
          <span className="flex-1">{title}</span>
          {onPhoto && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
                className="p-2 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-600 transition-colors"
                title={`Take photo — ${title}`}
                data-testid={`section-camera-${(sectionId || title).toLowerCase().replace(/\s+/g, '-')}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>
              </button>
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => { onPhoto(e, title); e.target.value = ''; }}
              />
            </>
          )}
          {collapsible && (
            <span className={`text-stone-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </span>
          )}
          {collapsible && isCollapsed && (
            <span className="text-xs bg-stone-100 text-stone-500 px-2 py-1 rounded">Click to expand</span>
          )}
        </h2>
      </div>
      {(!collapsible || !isCollapsed) && (
        <div className="px-6 pb-6">
          <div className="space-y-6">{children}</div>
        </div>
      )}
    </div>
  );
};

// ==================== FORM FIELD ====================
export const FormField = ({ label, required, children }) => (
  <div>
    <Label className="text-stone-700 font-medium text-base block mb-2">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </Label>
    {children}
  </div>
);

// ==================== INFO BUTTON ====================
export const InfoButton = ({ info, name }) => {
  const [open, setOpen] = useState(false);
  if (!info) return null;
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="absolute top-1 right-1 p-0.5 rounded-full bg-white/80 hover:bg-white text-stone-400 hover:text-blue-600 z-10 transition-colors"
        title={`What is ${name}?`}
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white p-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-lg">{name}</h3>
                  <p className="text-red-100 text-sm">{info.fullName}</p>
                </div>
                <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/20 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <h4 className="font-semibold text-stone-800 text-sm mb-1">What is it?</h4>
                <p className="text-sm text-stone-600">{info.description}</p>
              </div>
              <div>
                <h4 className="font-semibold text-stone-800 text-sm mb-1">What to look for</h4>
                <p className="text-sm text-stone-600">{info.signs}</p>
              </div>
              <div>
                <h4 className="font-semibold text-stone-800 text-sm mb-1">How to test / confirm</h4>
                <p className="text-sm text-stone-600">{info.test}</p>
              </div>
              <div className={`px-3 py-2 rounded-lg text-sm font-medium ${
                info.severity.startsWith('CRITICAL') ? 'bg-red-100 text-red-800' :
                info.severity.startsWith('Serious') ? 'bg-orange-100 text-orange-800' :
                info.severity.startsWith('Moderate') ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'
              }`}>
                Severity: {info.severity}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ==================== DISEASE & PEST INFO ====================
export const DISEASE_INFO = {
  'Sacbrood': {
    fullName: 'Sacbrood Virus (SBV)',
    description: 'A viral disease affecting bee larvae. Infected larvae fail to pupate and die, turning into a fluid-filled sac.',
    signs: 'Larvae appear as fluid-filled sacs when removed with tweezers. Dead larvae turn dark, forming a "Chinese slipper" shape. Scattered brood pattern.',
    test: 'Visual inspection: lift suspected larvae with tweezers — if they come out like a water-filled bag, it\'s Sacbrood.',
    severity: 'Moderate — usually self-resolving. Requeen if persistent.',
  },
  'DWV': {
    fullName: 'Deformed Wing Virus',
    description: 'A viral infection spread primarily by Varroa mites. Causes bees to emerge with crumpled, stunted wings, making them unable to fly.',
    signs: 'Newly emerged bees with shrivelled, deformed wings. Bees crawling on landing board unable to fly. Shortened abdomen.',
    test: 'Visual inspection of emerging bees. High mite loads correlate with DWV. Check for crawling bees at the hive entrance.',
    severity: 'Serious — indicates high Varroa load. Treat mites immediately.',
  },
  'EFB': {
    fullName: 'European Foulbrood',
    description: 'A bacterial disease (Melissococcus plutonius) affecting young larvae. Larvae die before cells are capped.',
    signs: 'Twisted, discoloured larvae (yellow-brown) in uncapped cells. Sour smell. Patchy, irregular brood pattern.',
    test: 'Visual: look for twisted, melted-looking larvae in open cells. Lab testing for confirmation. EFB field test kits available.',
    severity: 'Serious — often treatable with requeening and antibiotics. Notifiable in many regions.',
  },
  'AFB': {
    fullName: 'American Foulbrood',
    description: 'A highly contagious, fatal bacterial disease (Paenibacillus larvae). Spores can survive 50+ years. Notifiable disease.',
    signs: 'Sunken, perforated cell caps. Dark, ropy larval remains (matchstick/rope test positive). Foul smell. Scale on cell walls.',
    test: 'Matchstick test: insert a matchstick into a suspect cell, twist, and pull out slowly. If the remains stretch 2-3cm in a ropy thread, suspect AFB. Confirm with lab test or field kit.',
    severity: 'CRITICAL — burn all equipment. Notify authorities. Do NOT reuse frames or boxes.',
  },
  'Chalkbrood': {
    fullName: 'Chalkbrood (Ascosphaera apis)',
    description: 'A fungal disease that mummifies bee larvae. Common in cool, damp conditions or stressed colonies.',
    signs: 'Hard, chalk-white or grey/black mummified larvae in cells or on the bottom board/landing board. Shotgun brood pattern.',
    test: 'Visual inspection: look for white/grey hard "chalk" mummies in cells or on the ground in front of the hive.',
    severity: 'Moderate — improve ventilation, requeen with hygienic stock. Usually resolves in warm weather.',
  },
  'BPMS': {
    fullName: 'Black Queen Cell Virus / Bee Paralysis (Chronic & Acute)',
    description: 'A group of paralysis viruses (CBPV/ABPV) causing trembling, hairless black bees, and colony decline. Spread by Varroa and bee-to-bee contact.',
    signs: 'Trembling, shaking bees on frames. Hairless, dark/shiny "black" bees. Bees crawling unable to fly. Dysentery. Rapid colony decline.',
    test: 'Visual: look for trembling bees, hairless shiny black bees on frames. Clusters of sick bees at entrance being rejected by guards.',
    severity: 'Serious — treat Varroa, requeen with resistant stock. No direct treatment for the virus.',
  },
};

export const PEST_INFO = {
  'Ants': {
    fullName: 'Ant Infestation',
    description: 'Various ant species can invade hives to steal honey and disturb bees. Large infestations can cause absconding.',
    signs: 'Ant trails leading to/into hive. Ants inside the hive on frames. Bees agitated or fanning excessively.',
    test: 'Visual inspection around hive stand and inside the hive. Check under the lid and around entry points.',
    severity: 'Low-Moderate — use ant traps, oil moats on hive stands, or diatomaceous earth.',
  },
  'Mites': {
    fullName: 'Varroa Destructor Mites',
    description: 'The most destructive honey bee parasite worldwide. Feeds on fat bodies and transmits viruses (DWV, ABPV). Can collapse a colony within 1-2 seasons if untreated.',
    signs: 'Red-brown oval mites on bees or in drone brood. Deformed wings (DWV). Patchy brood. Weakening colony.',
    test: 'Alcohol wash or sugar roll: collect ~300 bees (½ cup), wash in alcohol or roll in powdered sugar, count mites. >3% infestation = treat immediately.',
    severity: 'CRITICAL — monitor monthly. Treat when threshold exceeded. Rotate treatment methods.',
  },
  'Mice': {
    fullName: 'Mouse Infestation',
    description: 'Mice enter hives in autumn/winter for warmth and food. They destroy comb, eat honey/pollen, and disturb the cluster.',
    signs: 'Chewed comb and frames. Mouse droppings on bottom board. Shredded material (nesting). Bees agitated in winter.',
    test: 'Visual inspection: look for droppings, chewed comb, nesting material. Check entrance for gnaw marks.',
    severity: 'Moderate — install mouse guards in autumn before temperatures drop. Entrance reducers help too.',
  },
  'Wax Moths': {
    fullName: 'Greater Wax Moth (Galleria mellonella)',
    description: 'Moth larvae tunnel through comb, destroying it with silk webbing. Primarily affects weak colonies or unprotected stored comb.',
    signs: 'Silk webbing and tunnels through comb. Small dark frass (droppings). Cocoons on frame edges/walls. Destroyed comb.',
    test: 'Visual: check for webbing across frames, tunnels in comb, and white larvae burrowing through wax.',
    severity: 'Moderate — strengthen colony. Freeze frames to kill eggs/larvae. Keep strong colonies.',
  },
  'SHB': {
    fullName: 'Small Hive Beetle (Aethina tumida)',
    description: 'A beetle pest that lays eggs in the hive. Larvae tunnel through comb, fermenting honey and causing "slime-out." Native to sub-Saharan Africa.',
    signs: 'Small dark beetles running on frames (they hide from light). Slimy, fermented honey. Larvae crawling in comb. Bees corralling beetles.',
    test: 'Visual: open hive and watch for small dark beetles scurrying away from light. Check bottom board for larvae. Use beetle traps.',
    severity: 'Serious — use beetle traps, reduce hive space, keep colonies strong. Can destroy weak colonies.',
  },
};
